import { Component, computed, input, output } from '@angular/core';

/**
 * Course selection card component for online quote tool
 *
 * Features a vertical layout optimized for course selection:
 * - Title at the top
 * - Content area for description/details (projected via ng-content)
 * - Footer with subtitle and radio button on the same row
 *
 * Layout Structure:
 * ┌─────────────────────┐
 * │ Title               │
 * ├─────────────────────┤
 * │ Content (projected) │
 * ├─────────────────────┤
 * │ Subtitle       (o)  │ ← Footer: subtitle + radio
 * └─────────────────────┘
 *
 * @example
 * <lx-course-selection-card
 *   title="Standard Course"
 *   subtitle="20 hours/week"
 *   [option]="courseData"
 *   [selectedOption]="currentSelection"
 *   (selectedOptionChanged)="onCourseSelect($event)"
 * >
 *   <div>Course description content</div>
 * </lx-course-selection-card>
 */
@Component({
  standalone: true,
  selector: 'lx-course-selection-card',
  templateUrl: './course-selection-card.component.html',
  styleUrls: ['./course-selection-card.component.scss'],
})
export class CourseSelectionCardComponent<T> {
  /**
   * Main title displayed at the top of the card
   */
  title = input.required<string>();

  /**
   * Subtitle displayed in the footer alongside the radio button
   */
  subtitle = input<string>('');

  /**
   * The option value this card represents
   */
  option = input.required<T>();

  /**
   * Currently selected option for comparison
   */
  selectedOption = input<T | undefined>(undefined);

  /**
   * Emits when this card is selected
   */
  selectedOptionChanged = output<T>();

  /**
   * Computed signal: true if this card's option is currently selected
   */
  readonly selected = computed(() => this.selectedOption() === this.option());

  /**
   * Handles card selection
   * Emits the option value when the card is clicked or activated via keyboard
   */
  protected selectOption(): void {
    this.selectedOptionChanged.emit(this.option());
  }
}
